import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

// Empty baseURL = same-origin. In dev, Vite proxies /api, /oauth2, /oauth, /login
// to localhost:8880 (see vite.config.ts). In production, Traefik routes
// app.merkflow.eu/api/* to Spring Boot. VITE_API_BASE_URL can override this
// (useful if running the frontend against the prod API in dev).
// `api` is intentionally not exported as an `AxiosInstance` named `default`
// — every provider-scoped module in this file uses it locally. Exported
// here (plain const, not default) so feature modules that prefer to live
// in their own file (e.g. `pages/admin/mock/mockApi.ts`) can still reuse
// the JWT interceptor + 401 logout wiring without rebuilding it.
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '',
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Token management.
 *
 * Single source of truth is the Zustand auth store (persisted under
 * `merkflow-auth` in localStorage). `setToken` / `getToken` are thin
 * wrappers that exist because the pre-migration codebase called them
 * everywhere — rewriting every call site is out of scope.
 *
 * We ALSO mirror the token to the legacy `token` localStorage key because:
 *   1. mcp-server/ reads it
 *   2. any stray pre-migration code still looking at localStorage.token works
 * Writing to the store is enough for React (AuthGuard, UI, etc.) because the
 * Zustand persist middleware handles `merkflow-auth` automatically.
 */
export const setToken = (t: string | null) => {
  useAuthStore.getState().setToken(t);
  if (typeof window !== 'undefined') {
    if (t) localStorage.setItem('token', t);
    else localStorage.removeItem('token');
  }
};

export const getToken = (): string | null => {
  // Zustand is cheap to read; no need to cache in a module-level var.
  const fromStore = useAuthStore.getState().token;
  if (fromStore) return fromStore;
  // Fallback to the legacy key for any session that pre-dates the migration
  // (user had an old `token` set but no `merkflow-auth` blob yet). On first
  // successful request we hydrate the store so subsequent code sees it.
  if (typeof window !== 'undefined') {
    const legacy = localStorage.getItem('token');
    if (legacy) {
      useAuthStore.getState().setToken(legacy);
      return legacy;
    }
  }
  return null;
};

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  // Send the UI language so the backend can localize error messages
  const lang = localStorage.getItem('merkflow_ui_lang') ?? 'nl';
  config.headers['Accept-Language'] = lang;
  return config;
});

// Global response interceptor — cross-cutting HTTP policy (402 plan/trial
// fan-out, 403 toast/modal routing with the background-GET guard, 401
// logout+redirect, deferred 5xx toast with __handled opt-out). Lives here
// beside the request interceptor so it registers because this module owns
// the axios instance, not as a side-effect of importing a domain module.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    if (status === 402 && typeof window !== 'undefined') {
      // V48 — distinguish trial-expired (full-screen paused overlay) from
      // per-feature limit exceeded (per-feature upgrade modal).
      // The backend WorkspaceWriteGateFilter (added later) emits
      // `trial_expired` (auto-flipped on first write after expiry) and
      // `workspace_paused` (already-paused workspace). Both surface
      // the same paused-overlay UX — they describe the same "you
      // can't write here" state to the user.
      const data = error.response?.data;
      if (data?.error === 'account_waitlisted') {
        // Waitlisted account hit a product API directly — park them on the
        // waiting-list screen (the DashboardLayout guard normally gets here
        // first, but a deep-linked/raced call can land here).
        if (window.location.pathname !== '/waitlisted') {
          window.location.assign('/waitlisted');
        }
      } else if (data?.error === 'trial_expired' || data?.error === 'workspace_paused') {
        window.dispatchEvent(new CustomEvent('trial-expired', { detail: data }));
      } else {
        window.dispatchEvent(new CustomEvent('plan-limit-exceeded', { detail: data }));
      }
    }
    if (status === 403 && typeof window !== 'undefined') {
      // Three sub-cases for 403:
      //   1. `feature_not_available` — backend AiVisibilityGate /
      //      similar service-layer flag check fired. Open the upgrade
      //      modal via the same `feature-not-available` event the
      //      frontend `<UpgradeLockedWrapper>` uses, so both proactive
      //      locked-CTA clicks and reactive backend rejections land on
      //      one listener.
      //   2. `INSUFFICIENT_ROLE` — workspaceSecurity.isOwner check on
      //      a non-owner. Toast since the user can't do anything about
      //      it from a modal.
      //   3. Anything else — generic forbidden toast, but ONLY for
      //      user-initiated mutations. Background GETs (analytics,
      //      stats, brand list, etc.) fan out in parallel on every
      //      page load; toasting each of them on a 403 floods the
      //      screen. The query already error-states in the UI — that
      //      is the right surface for passive fetches.
      const data = error.response?.data;
      const method = (error.config?.method ?? 'get').toLowerCase();
      const isMutation = method !== 'get' && method !== 'head' && method !== 'options';
      if (data?.error === 'feature_not_available') {
        // Modal only fires on user-initiated mutations (clicked button).
        // Background GETs that 403 with feature_not_available — e.g.
        // CompetitorManager polling on a paused workspace — already
        // render an inline upgrade card on the page. Popping a global
        // modal on top of that is duplicate noise (Bug 1, 2026-05-10).
        if (isMutation) {
          window.dispatchEvent(new CustomEvent('feature-not-available', { detail: data }));
        }
      } else if (data?.error === 'INSUFFICIENT_ROLE' || data?.error === 'insufficient_role') {
        toast.error(i18n.t('errors.ownerOnly'));
      } else if (isMutation) {
        toast.error(i18n.t('errors.forbidden'));
      }
    }
    if (status === 401 && typeof window !== 'undefined') {
      // Token expired / invalid — wipe auth state and bounce to login.
      // Skip the bounce if we're already on a public auth page to avoid loops.
      const path = window.location.pathname;
      const publicPaths = [
        '/login',
        '/signup',
        '/auth/signin',
        '/auth/register',
        '/auth/callback',
        '/auth/forgot-password',
        '/auth/reset-password',
      ];
      if (!publicPaths.some((p) => path.startsWith(p))) {
        useAuthStore.getState().logout();
        window.localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    if (typeof status === 'number' && status >= 500 && typeof window !== 'undefined') {
      // Defer the toast 100ms to give component-level catch handlers a
      // chance to mark the error as `__handled` (e.g. a page that
      // already shows its own error UI for a 500 from a specific
      // endpoint and doesn't want a duplicate global toast). Without
      // this guard every 5xx pops both a custom error UI AND a
      // generic toast — noisy and inconsistent. Callers opt out with:
      //
      //   .catch((err) => { (err as any).__handled = true; ... })
      //
      // Console-error in any case so we don't lose visibility on
      // surfacing-suppressed 5xx during debugging.
      const errorWithFlag = error as { __handled?: boolean };
      console.error('[api] 5xx', status, error.config?.url, error.response?.data);
      window.setTimeout(() => {
        if (!errorWithFlag.__handled) {
          toast.error(i18n.t('errors.serverError'));
        }
      }, 100);
    }
    return Promise.reject(error);
  },
);
